import { useEffect, useState, createContext, SetStateAction, Dispatch, ReactNode } from "react";
import { lensWildcardPubnubChannel } from "Backend";
import { useAppDispatch } from "redux/hooks";
import { usePubNub } from "pubnub-react";
import { MessageEvent } from "pubnub";
import { fetchForm } from "redux/slices/form.slice";
import { Lens } from "generated/graphql";
export type PubnubListener = { message: (evt: MessageEvent) => void };

export type LensRefreshCallbackType = (_: { lens: Lens; fieldIds: number[] }) => void;

export const LensRefreshAlertContext = createContext<
  Dispatch<SetStateAction<LensRefreshCallbackType | undefined>> | undefined
>(undefined);

interface LensRefreshAlertProviderProps {
  children: ReactNode;
  lens: Lens;
  defaultCallback?: LensRefreshCallbackType;
}
export function LensRefreshAlertProvider({ children, lens, defaultCallback }: LensRefreshAlertProviderProps) {
  const pubnub = usePubNub();
  const lensId = lens?.id;
  const dispatch = useAppDispatch();
  const [callback, setCallback] = useState<LensRefreshCallbackType | undefined>(() => defaultCallback);

  const listener: PubnubListener = {
    message: (evt: MessageEvent) => {
      if (callback) {
        try {
          const payload: { lensId: number; fieldIds: number[] } = JSON.parse(evt.message);
          const fieldIds = payload.fieldIds;
          if (fieldIds !== undefined) {
            callback({ lens, fieldIds });
          }
        } catch (error) {
          console.error("Failed to parse JSON:", error);
        }
      } else {
        dispatch(fetchForm({ lensId }));
      }
    },
  };

  useEffect(() => {
    if (lensId === undefined) return;
    pubnub.addListener(listener);
    pubnub.subscribe({ channels: [lensWildcardPubnubChannel(lensId)] });

    return () => {
      pubnub.removeListener(listener);
      pubnub.unsubscribe({ channels: [lensWildcardPubnubChannel(lensId)] });
    };
  }, [lensId, callback, pubnub]);

  return <LensRefreshAlertContext.Provider value={setCallback}>{children}</LensRefreshAlertContext.Provider>;
}
